import type { LineageDag, LineageEdge, LineageLogEntry } from "types/lineage.types"

/**
 * The command log of a schema 4 lineage DAG, grouped by the API call (create or run) each command
 * came from. A call is what the client sent in one go: the data load, or one script after FOR and
 * DEFINE were expanded.
 */

export interface LineageCallEntry {
  /** Position of the entry in the DAG's `log` (what an edge's `entry` points at) */
  index: number
  entry: LineageLogEntry
}

export interface LineageCall {
  /** The engine's call number */
  call: number
  entries: LineageCallEntry[]
  /** "error" when one of its commands failed (the last one logged: the rest never ran) */
  outcome: "ok" | "error"
  /** The call only loaded data (the create, or a script that was only a PROJECT) */
  dataLoad: boolean
}

export const isDataLoad = (e: LineageLogEntry): boolean => e.command === "PROJECT"

// One logged command as the script line read: the command and its params joined by spaces. A data
// load has no params; it shows as PROJECT.
export const commandLine = (e: LineageLogEntry): string => [e.command, ...(e.params ?? [])].join(" ")

// The log's calls in order. Entries of one call are consecutive; a new call number starts a new call.
export const groupLogByCall = (log: LineageLogEntry[]): LineageCall[] => {
  const calls: LineageCall[] = []
  log.forEach((entry, index) => {
    let last = calls[calls.length - 1]
    if (!last || last.call !== entry.call) {
      last = { call: entry.call, entries: [], outcome: "ok", dataLoad: true }
      calls.push(last)
    }
    last.entries.push({ index, entry })
    if (entry.outcome === "error") last.outcome = "error"
    if (!isDataLoad(entry)) last.dataLoad = false
  })
  return calls
}

// A call's commands as script text, one per line.
export const callText = (call: LineageCall): string =>
  call.entries.map(({ entry }) => commandLine(entry)).join("\n")

/**
 * One call of the session as the trace reads it, whatever the schema: a call of the command log
 * (schema 4) or a script of the script log (schema 3).
 */
export interface SessionCall {
  /** "create" loaded the data, "run" ran a script */
  kind: "create" | "run"
  /** Schema 4: its commands, one per line (see `callText`); schema 3: the script as it was sent */
  text: string
  outcome: "ok" | "error"
  error?: string
  /** Its entries in the command log (schema 4) */
  entries?: LineageCallEntry[]
}

// The session's calls in order: the command log's calls when the DAG has one, else its scripts.
export const sessionCalls = (dag: Pick<LineageDag, "log" | "scripts">): SessionCall[] =>
  dag.log
    ? groupLogByCall(dag.log).map((c) => ({
        kind: c.dataLoad ? "create" : "run",
        text: callText(c),
        outcome: c.outcome,
        error: c.entries.find(({ entry }) => entry.error)?.entry.error,
        entries: c.entries,
      }))
    : (dag.scripts ?? []).map((s) => ({
        kind: s.kind,
        text: s.kind === "create" ? "" : (s.script ?? ""),
        outcome: s.outcome,
        error: s.error,
      }))

/**
 * The call that recorded an edge, as a position in `sessionCalls`: through the edge's log entry
 * (schema 4) or its script (schema 3). Undefined when the DAG does not say.
 */
export const edgeCallOf = (dag: Pick<LineageDag, "log">): ((edge: LineageEdge) => number | undefined) => {
  if (!dag.log) return (edge) => edge.script
  const callOfEntry: number[] = []
  groupLogByCall(dag.log).forEach((c, i) => c.entries.forEach(({ index }) => (callOfEntry[index] = i)))
  return (edge) => (edge.entry === undefined ? undefined : callOfEntry[edge.entry])
}
